<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  Link2,
  FileUp,
  ListChecks,
  ChevronDown,
  X,
  Upload,
  AlertCircle,
  Magnet,
} from 'lucide-vue-next'
import Modal from '@/components/ui/Modal.vue'
import Button from '@/components/ui/Button.vue'
import Input from '@/components/ui/Input.vue'
import Tabs from '@/components/ui/Tabs.vue'
import type { TabItem } from '@/components/ui/Tabs.vue'
import UploadModal from '@/components/settings/UploadModal.vue'
import { useTaskActions } from '@/composables/useTaskActions'
import { useToast } from '@/composables/useToast'
import { addTask, type AddTaskPayload } from '@/api/tasks'
import { parseMagnet } from '@/composables/useMagnetParser'

interface Props {
  modelValue: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  added: []
}>()

const { t } = useI18n()
const toast = useToast()
const actions = useTaskActions()

type TabKey = 'url' | 'torrent' | 'metalink' | 'magnet'

const tab = ref<TabKey>('url')
const submitting = ref(false)

const urlsText = ref('')
const torrentFile = ref<File | null>(null)
const torrentB64 = ref<string>('')
const torrentFileName = ref('')
const metalinkFile = ref<File | null>(null)
const metalinkText = ref('')
const magnetText = ref('')
const showUploadModal = ref(false)

const optionsOpen = ref(false)

interface OptionState {
  path: string
  out: string
  user: string
  password: string
  referer: string
  cookie: string
  split: string
  maxConnectionPerServer: string
  header: string
  checksum: string
  selectFile: string
  startAt: string
  /** v3.19.37: 任务分类 (列表筛选) */
  category: string
  /** v3.19.37: 每任务代理 (http:// 或 socks5://) */
  proxy: string
}

function emptyOptions(): OptionState {
  return {
    path: '',
    out: '',
    user: '',
    password: '',
    referer: '',
    cookie: '',
    split: '',
    maxConnectionPerServer: '',
    header: '',
    checksum: '',
    selectFile: '',
    startAt: '',
    category: '',
    proxy: '',
  }
}

const options = reactive<OptionState>(emptyOptions())

const tabItems = computed<TabItem[]>(() => [
  { key: 'url', label: t('tasks.addTab.url'), badge: urlsText.value ? lineCount.value : undefined },
  { key: 'torrent', label: t('tasks.addTab.torrent'), badge: torrentFile.value ? '1' : undefined },
  { key: 'metalink', label: t('tasks.addTab.metalink'), badge: metalinkFile.value ? '1' : undefined },
  { key: 'magnet', label: t('tasks.addTab.magnet'), badge: magnetParsed.value.valid ? '1' : undefined },
])

const lineCount = computed(() => {
  return urlsText.value
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean).length
})

const urlList = computed(() => {
  return urlsText.value
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
})

// Live magnet preview. We trust the parser to flag `valid` for any input we
// want to submit — everything else is just informational preview text.
const magnetParsed = computed(() => parseMagnet(magnetText.value))
// select-file 仅对磁力/种子目标有意义; URL 页签里手填 magnet: 也算。
const isMagnetTarget = computed(() =>
  tab.value === 'magnet' ||
  tab.value === 'torrent' ||
  (tab.value === 'url' && /^magnet:/im.test(urlsText.value)),
)

const canSubmit = computed(() => {
  if (tab.value === 'url') return urlList.value.length > 0
  if (tab.value === 'torrent') return torrentB64.value.length > 0
  if (tab.value === 'metalink') return metalinkText.value.trim().length > 0
  if (tab.value === 'magnet') return magnetParsed.value.valid
  return false
})

function reset(): void {
  tab.value = 'url'
  urlsText.value = ''
  torrentFile.value = null
  torrentB64.value = ''
  torrentFileName.value = ''
  metalinkFile.value = null
  metalinkText.value = ''
  magnetText.value = ''
  Object.assign(options, emptyOptions())
  optionsOpen.value = false
}

watch(
  () => props.modelValue,
  (v) => {
    if (v) {
      // keep previous values for convenience (don't reset)
    } else {
      // small delay to let exit animation finish
      window.setTimeout(reset, 250)
    }
  },
)

/**
 * After UploadModal uploads a file, automatically submit a download task for
 * the freshly-uploaded URL. Spec wants the upload → addTask chain to be
 * friction-free: the user's "I picked this file" intent already implies
 * "create a download for it".
 *
 * UploadModal emits `uploaded` with no payload when called from FilesTab —
 * we only auto-submit when we got a URL back.
 */
async function onFileUploaded(payload?: { url: string; name: string }): Promise<void> {
  if (!payload?.url || submitting.value) return
  submitting.value = true
  try {
    const opt = buildOption()
    const urls = [payload.url]
    const payload2: AddTaskPayload = opt
      ? { type: 'uri', urls, option: opt }
      : { type: 'uri', urls }
    const res = await addTask(payload2)
    toast.success(t('tasks.toast.added', { gid: res.gid }))
    actions.invalidate()
    // Tell the parent view to refresh focus; matches the URL/torrent/metalink
    // submit() paths so behavior stays uniform.
    emit('added')
    emit('update:modelValue', false)
    // Re-open the upload modal? No — user just wants to upload once per open.
  } catch {
    /* toast already shown by api client */
  } finally {
    submitting.value = false
  }
}

function openUploadModal(): void {
  showUploadModal.value = true
}

function buildOption(): Record<string, unknown> | undefined {
  const out: Record<string, unknown> = {}
  if (options.path) out.dir = options.path
  if (options.out) out.out = options.out
  if (options.user) out.header = prependIfMissing(options.header, `User-Agent: ${options.user}`)
  if (options.user) out['http-user'] = options.user
  if (options.password) out['http-passwd'] = options.password
  if (options.referer) out.referer = options.referer
  if (options.cookie) out['load-cookies'] = options.cookie
  if (options.split) out['split'] = options.split
  if (options.maxConnectionPerServer) out['max-connection-per-server'] = options.maxConnectionPerServer
  if (options.header) out.header = options.header
  // v3.13.92: 新增能力入口 (后端 afd.addUri 已消费)
  if (options.checksum) out.checksum = options.checksum
  if (options.selectFile) out['select-file'] = options.selectFile
  if (options.startAt) out['start-at'] = options.startAt
  // v3.19.37: 分类 + 每任务代理
  if (options.category.trim()) out.category = options.category.trim()
  if (options.proxy.trim()) out.proxy = options.proxy.trim()
  return Object.keys(out).length > 0 ? out : undefined
}

function prependIfMissing(existing: string, addition: string): string {
  if (!existing) return addition
  if (existing.includes(addition.split(':')[0]!)) return existing
  return `${addition}\n${existing}`
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = (): void => reject(reader.error ?? new Error('read failed'))
    reader.onload = (): void => {
      const result = reader.result
      if (typeof result === 'string') {
        // strip data URL prefix
        const idx = result.indexOf(',')
        resolve(idx >= 0 ? result.slice(idx + 1) : result)
      } else {
        resolve('')
      }
    }
    reader.readAsDataURL(file)
  })
}

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = (): void => reject(reader.error ?? new Error('read failed'))
    reader.onload = (): void => {
      resolve(typeof reader.result === 'string' ? reader.result : '')
    }
    reader.readAsText(file)
  })
}

async function onTorrentSelected(e: Event): Promise<void> {
  const input = e.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return
  if (!/\.torrent$/i.test(file.name)) {
    toast.error(t('tasks.addDialog.errors.invalidTorrent'))
    return
  }
  torrentFile.value = file
  torrentFileName.value = file.name
  try {
    torrentB64.value = await readFileAsBase64(file)
  } catch {
    toast.error(t('tasks.addDialog.errors.readFailed'))
    torrentB64.value = ''
  }
}

function clearTorrent(): void {
  torrentFile.value = null
  torrentB64.value = ''
  torrentFileName.value = ''
}

async function onMetalinkSelected(e: Event): Promise<void> {
  const input = e.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return
  metalinkFile.value = file
  try {
    metalinkText.value = await readFileAsText(file)
  } catch {
    toast.error(t('tasks.addDialog.errors.readFailed'))
    metalinkText.value = ''
  }
}

function clearMetalink(): void {
  metalinkFile.value = null
  metalinkText.value = ''
}

function close(): void {
  if (submitting.value) return
  emit('update:modelValue', false)
}

async function submit(): Promise<void> {
  if (!canSubmit.value || submitting.value) return
  submitting.value = true

  try {
    const opt = buildOption()
    let payload: AddTaskPayload
    if (tab.value === 'url') {
      payload = opt ? { type: 'uri', urls: urlList.value, option: opt } : { type: 'uri', urls: urlList.value }
    } else if (tab.value === 'torrent') {
      payload = opt
        ? { type: 'torrent', torrent: torrentB64.value, option: opt }
        : { type: 'torrent', torrent: torrentB64.value }
    } else if (tab.value === 'metalink') {
      payload = opt
        ? { type: 'metalink', metalink: metalinkText.value, option: opt }
        : { type: 'metalink', metalink: metalinkText.value }
    } else {
      // Magnet: send through the same URI path. AFD accepts magnet:?xt=...
      // URLs as first-class URIs, so no separate type or payload shape needed.
      const m = magnetParsed.value
      const urls = m.valid ? [magnetText.value.trim()] : []
      payload = opt ? { type: 'uri', urls, option: opt } : { type: 'uri', urls }
    }

    const res = await addTask(payload)
    toast.success(t('tasks.toast.added', { gid: res.gid }))
    actions.invalidate()
    emit('added')
    emit('update:modelValue', false)
  } catch {
    /* toast already shown by api client */
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <Modal
    :model-value="modelValue"
    :title="t('tasks.addDialog.title')"
    size="xl"
    :close-on-backdrop="!submitting"
    @update:model-value="(v) => emit('update:modelValue', v)"
  >
    <div class="space-y-4">
      <Tabs v-model="tab" :items="tabItems" variant="line" />

      <!-- URL tab -->
      <div v-if="tab === 'url'" class="space-y-2">
        <div class="flex items-center justify-between gap-2">
          <label class="block text-xs font-medium text-fg-muted">
            {{ t('tasks.addDialog.urlsLabel') }}
          </label>
          <button
            type="button"
            class="inline-flex items-center gap-1.5 rounded-md border border-border bg-bg-elevated px-2.5 py-1 text-xs font-medium text-fg-muted transition-colors hover:bg-bg-hover hover:text-fg"
            :disabled="submitting"
            :title="t('tasks.addDialog.uploadTriggerHint')"
            @click="openUploadModal"
          >
            <Upload :size="12" />
            {{ t('tasks.addDialog.uploadTrigger') }}
          </button>
        </div>
        <textarea
          v-model="urlsText"
          class="block w-full rounded-md border border-border bg-bg-surface px-3 py-2 text-sm text-fg placeholder:text-fg-subtle outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500 transition:border-color duration-fast"
          rows="5"
          :placeholder="t('tasks.addDialog.urlsPlaceholder')"
          spellcheck="false"
        />
        <p class="text-xs text-fg-muted">
          {{ t('tasks.addDialog.urlsHint', { count: lineCount }) }}
        </p>
      </div>

      <!-- Torrent tab -->
      <div v-else-if="tab === 'torrent'" class="space-y-2">
        <label class="block text-xs font-medium text-fg-muted">
          {{ t('tasks.addDialog.torrentLabel') }}
        </label>
        <div
          v-if="!torrentFile"
          class="flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-border bg-bg-elevated/40 px-6 py-8 text-center transition-colors hover:border-brand-500 hover:bg-brand-500/5"
        >
          <FileUp :size="28" class="text-fg-subtle" />
          <p class="text-sm text-fg-muted">
            {{ t('tasks.addDialog.dropTorrent') }}
          </p>
          <label>
            <input
              type="file"
              accept=".torrent"
              class="sr-only"
              @change="onTorrentSelected"
            />
            <span class="btn-base h-9 px-3 text-sm bg-bg-elevated text-fg hover:bg-bg-hover border border-border cursor-pointer">
              <Upload :size="14" />
              {{ t('tasks.addDialog.chooseFile') }}
            </span>
          </label>
        </div>

        <div
          v-else
          class="flex items-center justify-between gap-3 rounded-md border border-border bg-bg-elevated px-3 py-2"
        >
          <div class="flex min-w-0 items-center gap-2">
            <FileUp :size="16" class="shrink-0 text-brand-500" />
            <span class="truncate text-sm text-fg">{{ torrentFileName }}</span>
            <span class="text-xs text-fg-muted">
              ({{ Math.round((torrentFile.size ?? 0) / 1024) }} KB)
            </span>
          </div>
          <button
            type="button"
            class="rounded p-1 text-fg-muted hover:bg-bg-hover hover:text-danger"
            :aria-label="t('common.delete')"
            @click="clearTorrent"
          >
            <X :size="14" />
          </button>
        </div>
      </div>

      <!-- Metalink tab -->
      <div v-else-if="tab === 'metalink'" class="space-y-2">
        <label class="block text-xs font-medium text-fg-muted">
          {{ t('tasks.addDialog.metalinkLabel') }}
        </label>
        <div
          v-if="!metalinkFile"
          class="flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-border bg-bg-elevated/40 px-6 py-8 text-center transition-colors hover:border-brand-500 hover:bg-brand-500/5"
        >
          <ListChecks :size="28" class="text-fg-subtle" />
          <p class="text-sm text-fg-muted">
            {{ t('tasks.addDialog.dropMetalink') }}
          </p>
          <label>
            <input
              type="file"
              accept=".metalink,.meta4"
              class="sr-only"
              @change="onMetalinkSelected"
            />
            <span class="btn-base h-9 px-3 text-sm bg-bg-elevated text-fg hover:bg-bg-hover border border-border cursor-pointer">
              <Upload :size="14" />
              {{ t('tasks.addDialog.chooseFile') }}
            </span>
          </label>
        </div>

        <div
          v-else
          class="flex items-center justify-between gap-3 rounded-md border border-border bg-bg-elevated px-3 py-2"
        >
          <div class="flex min-w-0 items-center gap-2">
            <ListChecks :size="16" class="shrink-0 text-brand-500" />
            <span class="truncate text-sm text-fg">
              {{ metalinkFile?.name ?? 'metalink' }}
            </span>
            <span class="text-xs text-fg-muted">
              ({{ Math.round((metalinkFile?.size ?? 0) / 1024) }} KB)
            </span>
          </div>
          <button
            type="button"
            class="rounded p-1 text-fg-muted hover:bg-bg-hover hover:text-danger"
            :aria-label="t('common.delete')"
            @click="clearMetalink"
          >
            <X :size="14" />
          </button>
        </div>
      </div>

      <!-- Magnet tab -->
      <div v-else-if="tab === 'magnet'" class="space-y-2">
        <label class="block text-xs font-medium text-fg-muted">
          {{ t('tasks.addDialog.magnetLabel') }}
        </label>
        <textarea
          v-model="magnetText"
          class="block w-full rounded-md border border-border bg-bg-surface px-3 py-2 text-sm text-fg placeholder:text-fg-subtle outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500 transition:border-color duration-fast font-mono"
          rows="3"
          :placeholder="t('tasks.addDialog.magnetPlaceholder')"
          spellcheck="false"
        />

        <div
          v-if="magnetText.trim()"
          class="rounded-md border border-border bg-bg-elevated/40 px-3 py-2 text-xs text-fg-muted space-y-1"
        >
          <div v-if="magnetParsed.valid" class="flex items-center gap-2 text-success">
            <Magnet :size="12" />
            <span>{{ t('tasks.addDialog.magnetValid') }}</span>
          </div>
          <div v-else class="flex items-center gap-2 text-danger">
            <AlertCircle :size="12" />
            <span>{{ t('tasks.addDialog.magnetInvalid') }}</span>
          </div>
          <dl v-if="magnetParsed.valid" class="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 font-mono">
            <dt class="text-fg-subtle">xt</dt>
            <dd class="break-all text-fg">{{ magnetParsed.infoHash }}</dd>
            <template v-if="magnetParsed.displayName">
              <dt class="text-fg-subtle">dn</dt>
              <dd class="break-all text-fg">{{ magnetParsed.displayName }}</dd>
            </template>
            <template v-if="magnetParsed.trackers.length">
              <dt class="text-fg-subtle">tr</dt>
              <dd>
                <ul class="space-y-0.5">
                  <li v-for="(tr, i) in magnetParsed.trackers" :key="i" class="break-all text-fg">
                    {{ tr }}
                  </li>
                </ul>
              </dd>
            </template>
          </dl>
        </div>

        <p class="text-xs text-fg-muted">
          {{ t('tasks.addDialog.magnetHint') }}
        </p>
      </div>

      <!-- Options (collapsible) -->
      <div class="rounded-md border border-border bg-bg-elevated/30">
        <button
          type="button"
          class="flex w-full items-center justify-between gap-2 px-3 py-2 text-sm font-medium text-fg-muted hover:text-fg"
          @click="optionsOpen = !optionsOpen"
        >
          <span class="flex items-center gap-2">
            <Link2 :size="14" />
            {{ t('tasks.addDialog.options') }}
          </span>
          <ChevronDown
            :size="14"
            :class="optionsOpen ? 'rotate-180 transition-transform' : 'transition-transform'"
          />
        </button>
        <div v-if="optionsOpen" class="border-t border-border p-3">
          <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label class="mb-1 block text-xs text-fg-muted">
                {{ t('tasks.addDialog.dir') }}
              </label>
              <Input v-model="options.path" :placeholder="t('tasks.addDialog.dirPlaceholder')" />
            </div>
            <div>
              <label class="mb-1 block text-xs text-fg-muted">
                {{ t('tasks.addDialog.out') }}
              </label>
              <Input v-model="options.out" :placeholder="t('tasks.addDialog.outPlaceholder')" />
            </div>
            <div>
              <label class="mb-1 block text-xs text-fg-muted">
                {{ t('tasks.addDialog.user') }}
              </label>
              <Input v-model="options.user" />
            </div>
            <div>
              <label class="mb-1 block text-xs text-fg-muted">
                {{ t('tasks.addDialog.password') }}
              </label>
              <Input v-model="options.password" type="password" />
            </div>
            <div>
              <label class="mb-1 block text-xs text-fg-muted">
                {{ t('tasks.addDialog.referer') }}
              </label>
              <Input v-model="options.referer" />
            </div>
            <div>
              <label class="mb-1 block text-xs text-fg-muted">
                {{ t('tasks.addDialog.split') }}
              </label>
              <Input v-model="options.split" placeholder="5" />
            </div>
            <div>
              <label class="mb-1 block text-xs text-fg-muted">
                {{ t('tasks.addDialog.category') }}
              </label>
              <Input v-model="options.category" :placeholder="t('tasks.addDialog.categoryPlaceholder')" />
            </div>
            <div class="md:col-span-2">
              <label class="mb-1 block text-xs text-fg-muted">
                {{ t('tasks.addDialog.proxy') }}
              </label>
              <Input v-model="options.proxy" placeholder="http://127.0.0.1:7890 或 socks5://…" />
            </div>
            <div class="md:col-span-2">
              <label class="mb-1 block text-xs text-fg-muted">
                {{ t('tasks.addDialog.cookie') }}
              </label>
              <textarea
                v-model="options.cookie"
                class="block w-full rounded-md border border-border bg-bg-surface px-3 py-2 text-sm text-fg placeholder:text-fg-subtle outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500 transition:border-color duration-fast"
                rows="2"
                spellcheck="false"
              />
            </div>
            <div class="md:col-span-2">
              <label class="mb-1 block text-xs text-fg-muted">
                {{ t('tasks.addDialog.header') }}
              </label>
              <textarea
                v-model="options.header"
                class="block w-full rounded-md border border-border bg-bg-surface px-3 py-2 text-sm text-fg placeholder:text-fg-subtle outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500 transition:border-color duration-fast"
                rows="2"
                spellcheck="false"
              />
            </div>
            <!-- v3.13.92: 校验和 / 定时下载 (磁力任务另有 select-file) —
                 后端已支持, 此前对话框缺入口 -->
            <div>
              <label class="mb-1 block text-xs text-fg-muted">
                {{ t('tasks.addDialog.checksum') }}
              </label>
              <input
                v-model="options.checksum"
                type="text"
                placeholder="sha-256=…"
                class="block w-full rounded-md border border-border bg-bg-surface px-3 py-2 text-sm text-fg placeholder:text-fg-subtle outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500 transition:border-color duration-fast"
                spellcheck="false"
              />
            </div>
            <div>
              <label class="mb-1 block text-xs text-fg-muted">
                {{ t('tasks.addDialog.startAt') }}
              </label>
              <input
                v-model="options.startAt"
                type="text"
                placeholder="+2h / 2026-12-01T02:00:00+08:00"
                class="block w-full rounded-md border border-border bg-bg-surface px-3 py-2 text-sm text-fg placeholder:text-fg-subtle outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500 transition:border-color duration-fast"
                spellcheck="false"
              />
            </div>
            <div v-if="isMagnetTarget">
              <label class="mb-1 block text-xs text-fg-muted">
                {{ t('tasks.addDialog.selectFile') }}
              </label>
              <input
                v-model="options.selectFile"
                type="text"
                placeholder="1-3,5"
                class="block w-full rounded-md border border-border bg-bg-surface px-3 py-2 text-sm text-fg placeholder:text-fg-subtle outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500 transition:border-color duration-fast"
                spellcheck="false"
              />
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="!canSubmit"
        class="flex items-center gap-2 rounded-md bg-warning/10 px-3 py-2 text-xs text-warning"
      >
        <AlertCircle :size="14" />
        <span>{{ t('tasks.addDialog.emptyWarn') }}</span>
      </div>
    </div>

    <template #footer>
      <Button variant="ghost" size="sm" :disabled="submitting" @click="close">
        {{ t('common.cancel') }}
      </Button>
      <Button :loading="submitting" :disabled="!canSubmit" size="sm" @click="submit">
        {{ t('tasks.addDialog.submit') }}
      </Button>
    </template>
  </Modal>

  <!-- Embedded upload modal. Lives next to the parent dialog so we can
       communicate via @uploaded (no global store needed). -->
  <UploadModal
    v-model="showUploadModal"
    @uploaded="onFileUploaded({ url: '', name: '' })"
  />
</template>